/* ---------------------------------------------------------------------------
   Remote component loader (NO unified routing)
   - Loads MF components explicitly
   - Sets MF component intent for access guard bypass
   - Fully backward-compatible with existing remotes
--------------------------------------------------------------------------- */

import { keycloak, pinia } from "../../../main";
import { useUserStore } from "@/lib/stores";
import { onBoardingService } from "@/lib/services/onBoardingService";

/* -----------------------
   Module Federation discovery + script fallback
------------------------- */

const _containerCache = new Map();

function injectScript(remoteUrl, options = {}) {
  const { crossorigin = true, id } = options;

  return new Promise((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("No document available"));
      return;
    }

    if (id) {
      const existing = document.getElementById(id);
      if (existing) {
        if (existing.loaded) return resolve();
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", reject);
        return;
      }
    }

    const script = document.createElement("script");
    script.src = remoteUrl;
    script.type = "text/javascript";
    if (crossorigin) script.crossOrigin = "anonymous";
    if (id) script.id = id;

    script.onload = () => {
      script.loaded = true;
      resolve();
    };
    script.onerror = reject;

    document.head.appendChild(script);
  });
}

function findGlobalContainer(remoteName) {
  if (typeof window === "undefined") return null;

  if (remoteName && window[remoteName] && typeof window[remoteName].get === "function") {
    return window[remoteName];
  }

  for (const key of Object.keys(window)) {
    try {
      const cand = window[key];
      if (cand && typeof cand.get === "function" && typeof cand.init === "function") {
        return cand;
      }
    } catch (e) {}
  }

  return null;
}

export async function loadRemoteContainer(remoteUrl, remoteName) {
  const cacheKey = `${remoteUrl}::${remoteName || ""}`;
  if (_containerCache.has(cacheKey)) return _containerCache.get(cacheKey);

  const scriptId = remoteName
    ? `remoteEntry-${remoteName}`
    : `remoteEntry-${btoa(remoteUrl).slice(0, 8)}`;

  await injectScript(remoteUrl, { id: scriptId });

  const container = findGlobalContainer(remoteName);
  if (!container) {
    throw new Error("Module Federation container not found");
  }

  try {
    if (window.__webpack_share_scopes__ && window.__webpack_share_scopes__.default) {
      await container.init(window.__webpack_share_scopes__.default);
    }
  } catch (e) {
    // ignore double init
  }

  _containerCache.set(cacheKey, container);
  return container;
}

export async function getModuleFromContainer(container, modulePath) {
  const factory = await container.get(modulePath);
  return factory();
}

/* -------------------------
   mountRemoteModule
--------------------------- */

export async function mountRemoteModule({
  remoteUrl,
  remoteName,
  modulePath,
  mountPointId,
  mountProps = {},
  mountMethodNames = ["mount", "mountWidget"],
} = {}) {
  if (!remoteUrl || !modulePath || !mountPointId) {
    throw new Error("remoteUrl, modulePath and mountPointId are required");
  }

  let el = document.getElementById(mountPointId);

  if (!el) {
    
    const getElement = () => {
      const outlet = document.getElementById("mfe-component-wrapper");

      // Retry until shell outlet is available
      if (!outlet) {
        setTimeout(getElement, 10);
        return;
      }

      // Clear previous MFE
      outlet.innerHTML = "";

      // Create new MFE mount node
      const mfeDiv = document.createElement("div");
      mfeDiv.id = mountPointId;
      outlet.appendChild(mfeDiv);

      return(mfeDiv);
    };
    el = getElement();
  }

  // 🔐 MF COMPONENT INTENT FLAG
  const prevState = window.history.state || {};
  window.history.replaceState(
    { ...prevState, __mfComponentRender: true },
    ""
  );

  try {
    const container = await loadRemoteContainer(remoteUrl, remoteName);
    const Module = await getModuleFromContainer(container, modulePath);

    for (const name of mountMethodNames) {
      if (Module && typeof Module[name] === "function") {
        const handle = await Module[name]({
          elId: mountPointId,
          el,
          ...mountProps,
        });

        if (handle && typeof handle.unmount === "function") {
          return handle;
        }

        return {
          async unmount() {
            try {
              if (typeof Module.unmount === "function") {
                await Module.unmount(mountProps);
              }
              while (el.firstChild) el.removeChild(el.firstChild);
            } catch (e) {
              console.warn("[mf] fallback unmount failed", e);
            }
          },
        };
      }
    }

    throw new Error("No supported mount method found");
  } finally {
    window.dispatchEvent(
      new CustomEvent('MFE_LOADED', {
        detail: false,
      })
    )
    // 🧹 CLEANUP FLAG
    const next = { ...(window.history.state || {}) };
    delete next.__mfComponentRender;
    window.history.replaceState(next, "");
  }
}

/* -------------------------
   mountRemoteByComponent
--------------------------- */

export async function mountRemoteByComponent({
  mountId,
  component,
  remoteUrl,
  remoteName,
  modulePath,
  mountMethodNames = ["mount", "mountWidget"],
  mountProps = {},
} = {}) {
  if (!mountId || !component || !remoteUrl) {
    throw new Error("mountId, component and remoteUrl are required");
  }

  return mountRemoteModule({
    remoteUrl,
    remoteName,
    modulePath,
    mountPointId: mountId,
    mountMethodNames,
    mountProps: {
      ...mountProps,
      component,
      params: {
        keycloak,
        pinia,
        shellStores: {
          auth: useUserStore,
        },
        onBoardingService,
      },
    },
  });
}
